import { t } from "i18next";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useFetch } from "../../../../hooks/useFetch";
import { FShift, ScreenType } from "../../../../types/types";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { Separator } from "../../../../ui/Separator";
import { Button } from "../../../../ui/Button";
import { useAppStore } from "../../../../stores/RootStore";
import { useOutletContext } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { EquipmentsTableData } from "../../../../types/formDataTypes";
import { DataTable } from "../../../../ui/DataTable/DataTable";
import { DateTime } from "luxon";
import { useScreenStore } from "../../../../stores/ScreenStore";

type Props = {
  updateMonitor: Function;
};

const ScreenEquipmentTab = ({ updateMonitor }: Props) => {
  const {monitor} = useScreenStore();
  const shifts = useAppStore((store) => store.shifts);
  const [shiftId, setShiftId] = useState("");

  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/directories/screens/${monitor?.id}/working_shifts`
  );

  const [{ response: deleteResponse, isLoading: deleteIsLoading, error: deleteError }, doFetchItemDelete] = useFetch(
    `/directories/screens/${monitor?.id}/working_shifts/${shiftId}`
  );

  useEffect(() => {
    if (!saveResponse) return;
    updateMonitor();
  }, [saveResponse]);

  useEffect(() => {
    if (!deleteResponse) return;
    updateMonitor();
  }, [deleteResponse]);

  let nodes = shifts?.filter(shift => shift.id != 'full').map((shift) => {
    return {
      id: shift.id,
      name: shift.name,
      start_at: shift.start_at,
      end_at: shift.end_at,
      working_shift_breaks: shift.working_shift_breaks,
    };
  });

  nodes = _.sortBy(nodes, []);
  let data = { nodes };

  const disconnectShift = (screenId: string) => {
    setShiftId(screenId);
    doFetchItemDelete({
      method: "DELETE",
    });
  };

  const connectShift = (id: string) => {
    doFetchItemSave({
      method: "POST",
      data: {
        data: {
          working_shift_id: id,
        },
      },
    });
  };

  const columns: ColumnDef<FShift>[] = [
    {
      header: t("title_name"),
      cell: (props) => <div className="font-medium">{props.row.original.name}</div>,
    },
    {
      header: `${t("start_at")}`,
      cell: ({ row }) => (
        <div className="font-medium">{`${row.original.start_at.toLocaleString(DateTime.TIME_24_SIMPLE)}`}</div>
      ),
    },
    {
      header: t("end_at"),
      cell: ({ row }) => (
        <div className="font-medium">{`${row.original.end_at.toLocaleString(DateTime.TIME_24_SIMPLE)}`}</div>
      ),
    },
    {
      accessorKey: "id",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-1 justify-end">
          {monitor?.attributes.working_shift_relations.find(
            (eq) => String(eq.working_shift?.id!) == row.original.id
          ) ? (
            <Button
              variant="destructive"
              size="xs"
              onClick={() => {
                disconnectShift(
                  String(
                    monitor?.attributes.working_shift_relations.find(
                      (eq) => String(eq.working_shift.id) == row.original.id
                    )?.id
                  )
                );
              }}
            >
              {t("disconnect")}
            </Button>
          ) : (
            <Button
              variant="default"
              size="xs"
              onClick={() => {
                connectShift(row.original.id);
              }}
            >
              {t("connect")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="relative flex flex-col w-full h-full overflow-hidden bg-background px-6">
        <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
          <div className="flex flex-col space-y-1">
            <span className="text-2xl font-semibold">{t('choose_shifts_for_screen')}</span>
          </div>
        </div>
        <ScrollArea className="px-2">
          <DataTable data={data.nodes} columns={columns} />
        </ScrollArea>
      </div>
    </>
  );
};

export default ScreenEquipmentTab;
